import { NodeType } from 'prosemirror-model';
import { sinkListItem, wrapInList } from 'prosemirror-schema-list';
import { Command, EditorState, TextSelection, Transaction } from 'prosemirror-state';
import { findParentNode } from 'prosemirror-utils';

import { isList } from '../../utils';

export const removeSlash: Command = (state, dispatch) => {
  // If command is fired, removeSlash would be true, and it will
  // remove the existing '/' in the editor.
  const parent = findParentNode((node) => !!node)(state.selection);
  if (parent && dispatch) {
    const subStringCursor = parent.node.textContent.substring(
      0,
      state.selection.$anchor.parentOffset,
    );
    const slashPosition = subStringCursor.lastIndexOf('/');
    if (slashPosition !== -1) {
      dispatch(
        state.tr.insertText(
          '',
          parent.pos + slashPosition + 1,
          parent.pos + subStringCursor.length + 1,
        ),
      );
    }

    return true;
  }

  return false;
};

export function createNewBlock(nodeType: NodeType, attrs?: Object) {
  return ((state, dispatch) => {
    if (dispatch) {
      dispatch(state.tr.replaceSelectionWith(nodeType.create(attrs)).scrollIntoView());

      return true;
    }

    return false;
  }) as Command;
}

export function appendBlockAtEnd(nodeType: NodeType, attrs?: Record<string, any>): Command {
  return (state, dispatch) => {
    if (!dispatch) return false;

    const { tr, doc, schema } = state;
    const endPos = doc.content.size;

    const node = nodeType.createAndFill(attrs);
    if (!node) return false;

    tr.insert(endPos, node);

    const resolvedPos = tr.doc.resolve(endPos + 1);
    tr.setSelection(TextSelection.near(resolvedPos));

    dispatch(tr.scrollIntoView());
    return true;
  };
}

// ref: https://github.com/outline/rich-markdown-editor/blob/636265c/src/commands/toggleList.ts#L7
export function toggleAddMenuList(listType: NodeType, itemType: NodeType) {
  return (state: EditorState, dispatch?: (tr: Transaction) => void) => {
    const { schema, selection } = state;
    const { $from, $to } = selection;
    const range = $from.blockRange($to);

    if (!range) {
      return false;
    }

    const parentList = findParentNode((node) => isList(node, schema))(selection);

    if (range.depth >= 1 && parentList && range.depth - parentList.depth <= 1) {
      if (parentList.node.type === listType) {
        return sinkListItem(itemType)(state, dispatch);
      }

      // If we are in the list already and changing the list type.
      if (isList(parentList.node, schema) && listType.validContent(parentList.node.content)) {
        const { tr } = state;
        tr.setNodeMarkup(parentList.pos, listType, parentList.node.attrs);

        if (dispatch) {
          dispatch(tr);
        }

        return true;
      }
    }

    return wrapInList(listType)(state, dispatch);
  };
}
